import React, { useState, useEffect } from 'react';
import { inputCls, labelCls } from '../shared';

const CATEGORIES = ['Risk', 'Delay', 'Bottleneck', 'Opportunity', 'General'];
const SEVERITY = ['Low', 'Medium', 'High', 'Critical'];

function severityColor(s) {
  if (s === 'Critical') return 'badge-danger';
  if (s === 'High') return 'badge-warning';
  if (s === 'Medium') return 'badge-primary';
  return 'badge-success';
}

function categoryColor(c) {
  if (c === 'Risk') return 'badge-danger';
  if (c === 'Delay') return 'badge-warning';
  if (c === 'Bottleneck') return 'badge-danger';
  if (c === 'Opportunity') return 'badge-success';
  return 'badge-neutral';
}

export default function InsightsPage({ counts = {}, setCounts }) {
  const [insights, setInsights] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState({ title: '', category: 'General', severity: 'Medium', programme: '', description: '' });

  useEffect(() => {
    if (setCounts) {
      setCounts(prev => ({ ...prev, insights: insights.length }));
    }
  }, [insights.length, setCounts]);

  const filtered = insights.filter(i => filter === 'All' || i.category === filter);

  const handleSubmit = e => {
    e.preventDefault();
    setInsights(prev => [{ ...form, id: Date.now(), date: new Date().toISOString().split('T')[0], author: 'Adani Admin' }, ...prev]);
    setForm({ title: '', category: 'General', severity: 'Medium', programme: '', description: '' });
    setShowForm(false);
  };

  const handleDelete = id => {
    setInsights(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">AI Insights</h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">Track risks, delays, and opportunities across programmes</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="topbar-filter">
            <option>All</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-[13px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Insight
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card-static rounded-2xl p-5 mb-5 animate-slide-up">
          <h3 className="text-[15px] font-semibold text-[var(--adani-text-primary)] mb-4">New Insight</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Brief insight title" required />
              </div>
              <div>
                <label className={labelCls}>Programme</label>
                <input value={form.programme} onChange={e => setForm(f => ({ ...f, programme: e.target.value }))} className={inputCls} placeholder="e.g. Mundra Port Expansion" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={inputCls + ' cursor-pointer'}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Severity</label>
                <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className={inputCls + ' cursor-pointer'}>
                  {SEVERITY.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputCls} rows={3} placeholder="Describe the insight, risk, or opportunity…" required />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-[13px]">Cancel</button>
              <button type="submit" className="btn-primary text-[13px]">Save Insight</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(insight => (
          <div key={insight.id} className="glass-card rounded-2xl px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <p className="text-[14px] font-medium text-[var(--adani-text-primary)]">{insight.title}</p>
                  <span className={`badge ${severityColor(insight.severity)}`}>{insight.severity}</span>
                  <span className={`badge ${categoryColor(insight.category)}`}>{insight.category}</span>
                </div>
                {insight.programme && (
                  <p className="text-[12px] text-[var(--adani-primary)] font-medium mt-1.5">{insight.programme}</p>
                )}
                <p className="text-[13px] text-[var(--adani-text-secondary)] mt-2 leading-relaxed">{insight.description}</p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-[11px] text-[var(--adani-text-tertiary)]">{insight.date}</span>
                  <span className="text-[11px] text-[var(--adani-text-tertiary)]">·</span>
                  <span className="text-[11px] text-[var(--adani-text-tertiary)]">{insight.author}</span>
                </div>
              </div>
              <button onClick={() => handleDelete(insight.id)} className="btn-icon shrink-0 text-[var(--adani-text-tertiary)] hover:text-[var(--adani-danger)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="glass-card-static rounded-2xl">
            <div className="empty-state">
              <p className="empty-state-title">No insights yet</p>
              <p className="empty-state-text">Click "Add Insight" to create your first one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
